/*
 * Wrapper component for PriceBookField that uses DealsService
 * This is specific to the NewDealForm and doesn't affect KeyDealsTab
 */
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useState } from 'react';
import { Utilities } from 'utilities/utilities';
import zeplinColor from 'theme/Color';
import { DealsService } from 'service/deals.service';
import { useEffect } from 'react';


const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
    },
  },
};

const DealsPriceBookField = ({
  handleInputChange,
  value,
  setPricebookName,
  modal,
}: {
  handleInputChange: (field: string, value: any) => void;
  setPricebookName: React.Dispatch<React.SetStateAction<string>>;
  modal: boolean;
  value: any;
}) => {
  const [selectedItems, setSelectedItems] = useState<{
    priceBook: string[];
  }>({
    priceBook: value?.pricebook2Id || value ? [value?.pricebook2Id || value] : ['none'],
  });
  const theme = useTheme();
  const [pricebook, setPricebook] = useState<any>({});
  const [loading, setLoading] = useState(false);
  
  // Sync selectedItems when value prop changes (for edit mode)
  useEffect(() => {
    const pricebookId = value?.pricebook2Id || value;
    if (pricebookId) {
      setSelectedItems({ priceBook: [pricebookId] });
    } else {
      setSelectedItems({ priceBook: ['none'] });
    }
  }, [value?.pricebook2Id, value]);
  
  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };
  
  const getPriceBook = async () => {
    try {
      setLoading(true);
      const res = await DealsService.getPricebooks();
      setPricebook(res);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    getPriceBook();
  }, []);

  useEffect(() => {
    setPricebookName(
      Utilities.getMatchingRecordTypeKey(selectedItems.priceBook[0], pricebook) || '',
    );
  }, [selectedItems.priceBook[0], pricebook, setPricebookName]);
  
  return (
    <Box sx={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
      <Typography variant='heading4'>Choose Price Book</Typography>
      <Typography variant='body2' color={zeplinColor.Neutral9}>
        Select a Price Book to associate with this deal. You can add products only from the Price
        Book you associate with this deal. Changing the Price Book deletes all products from the
        deal.
      </Typography>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel
          id='demo-multiple-name-label'
          sx={{
            '&.Mui-focused': { color: zeplinColor.Neutral9 },
          }}
        >
          Price book<span style={{ color: zeplinColor.Error }}></span>
        </InputLabel>
        <Select
          labelId='demo-multiple-name-label'
          id='demo-multiple-name'
          required={true}
          value={selectedItems.priceBook}
          onChange={(e) => {
            handleSelection(e, 'priceBook');
            // Map to priceBookId to match form field name
            handleInputChange('priceBookId', e.target.value);
          }}
          input={
            <OutlinedInput
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  color: zeplinColor.Neutral9,
                  borderColor: zeplinColor.Neutral9,
                },
              }}
              label='Price Book'
            />
          }
          renderValue={(selected) => {
            return loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                }}
              />
            ) : selected?.length ? (
              selected[0] === 'none' ? (
                'None'
              ) : Object.keys(pricebook)?.length &&
                Utilities.getMatchingRecordTypeKey(selectedItems.priceBook[0], pricebook) ? (
                Utilities.getMatchingRecordTypeKey(selectedItems.priceBook[0], pricebook)
              ) : (
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    width: '100%',
                  }}
                />
              )
            ) : (
              ''
            );
          }}
          MenuProps={MenuProps}
          IconComponent={KeyboardArrowDown}
        >
          <MenuItem key={'none'} value={'none'}>
            <Radio
              checked={
                selectedItems?.priceBook?.length
                  ? selectedItems?.priceBook?.indexOf('none') > -1
                  : false
              }
              sx={{
                color: zeplinColor.BasicBlack,
                '&.Mui-checked': {
                  color: zeplinColor.BasicBlack,
                },
              }}
            />
            ----None----
          </MenuItem>
          {Object.keys(pricebook)?.length > 0 &&
            Object.keys(pricebook)?.map((name: any) => (
              <MenuItem key={pricebook[name]} value={pricebook[name]}>
                <Radio
                  checked={
                    selectedItems?.priceBook?.length
                      ? selectedItems?.priceBook?.indexOf(pricebook[name]) > -1
                      : false
                  }
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': {
                      color: zeplinColor.BasicBlack,
                    },
                  }}
                />
                {name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default DealsPriceBookField;
